/**
 * "Advanced filters": every filter except the dates, in a panel that slides in
 * from the right. Choices are a draft until Apply, so changing several filters
 * costs one refetch, and Cancel / Escape / the backdrop leave the page as it was.
 * Only the filter groups the current page offers are shown (shared/tabFilters.js).
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useFilterOptions } from "../api/hooks.js";
import { FILTER_GROUPS } from "../../shared/tabFilters.js";
import { FILTER_LABELS } from "../lib/scope.js";
import { hasRegion } from "../../shared/tenants.js";
import { Button, Checkbox, Field, IconButton, Input, Segmented, Select } from "./ui/index.js";

const PROFILE = [
  ["specialty", "All specialities"],
  ["profession", "All professions"],
  ["country", "All countries"],
  ["state", "All states"],
  ["city", "All cities"],
];

const PLATFORMS = [
  { value: "", label: "App + web" },
  { value: "app", label: "App" },
  { value: "web", label: "Web" },
];

function Section({ title, hint, children }) {
  return (
    <fieldset className="border-b border-line px-5 py-5 last:border-0">
      <legend className="float-left mb-2.5 w-full text-[10.5px] font-semibold tracking-[0.12em] text-ink-3 uppercase">{title}</legend>
      {hint && <p className="clear-left -mt-1 mb-3 text-[11.5px] text-ink-3">{hint}</p>}
      <div className="clear-left flex flex-col gap-3.5">{children}</div>
    </fieldset>
  );
}

/** A select of the options for `k`, keeping a set value that is not (or no longer) among them. */
function OptionSelect({ value, options, all, onChange, disabled, label = (x) => x.value }) {
  return (
    <Select size="sm" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">{all}</option>
      {value && !options.some((x) => x.value === value) && <option value={value}>{value}</option>}
      {options.map((x) => (
        <option key={x.value} value={x.value}>
          {label(x)}
        </option>
      ))}
    </Select>
  );
}

/** The keys this drawer edits on a page showing `show` groups (plus includeTest). */
const draftKeys = (show) => show.flatMap((g) => FILTER_GROUPS[g] ?? []);

export function FilterDrawer({ f, show, open, onClose }) {
  const { filters, update } = f;
  const keys = draftKeys(show);
  const [draft, setDraft] = useState(null);
  const panel = useRef(null);
  const opts = useFilterOptions(filters.tenant, filters.from, filters.to);
  const o = (k) => opts.data?.[k] ?? [];

  // A fresh draft from the applied filters each time the drawer opens.
  useEffect(() => {
    if (!open) return undefined;
    setDraft({ ...Object.fromEntries(keys.map((k) => [k, filters[k] ?? ""])), includeTest: filters.includeTest });
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    // Focus the panel so keyboard users start inside it.
    requestAnimationFrame(() => panel.current?.querySelector("select, input, button")?.focus());
    return () => document.removeEventListener("keydown", onKey);
    // Re-initialise only on open; `filters` changing while open must not wipe the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || !draft) return null;

  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const changed = keys.some((k) => (draft[k] ?? "") !== (filters[k] ?? "")) || draft.includeTest !== filters.includeTest;
  const apply = () => {
    update(Object.fromEntries([...keys.map((k) => [k, draft[k]?.trim?.() ?? ""]), ["includeTest", draft.includeTest]]));
    onClose();
  };
  const reset = () => setDraft({ ...Object.fromEntries(keys.map((k) => [k, ""])), includeTest: false });

  // Rendered on <body>: an ancestor with a transform or backdrop-filter would
  // otherwise trap this fixed overlay inside itself.
  return createPortal(
    // Phones: a bottom sheet; wider screens: a panel from the right.
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-stretch sm:justify-end">
      <button type="button" aria-label="Close advanced filters" className="absolute inset-0 animate-fade-in cursor-default bg-[#0b1220]/35 backdrop-blur-[2px]" onClick={onClose} tabIndex={-1} />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="adv-filters-title"
        className="relative flex max-h-[88vh] w-full animate-slide-in-up flex-col rounded-t-2xl border-t border-line bg-surface-raised shadow-pop sm:h-full sm:max-h-none sm:w-[380px] sm:animate-slide-in-right sm:rounded-none sm:border-t-0 sm:border-l"
      >
        <span aria-hidden className="mx-auto mt-2 h-1 w-10 rounded-full bg-line-default sm:hidden" />
        <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div>
            <h2 id="adv-filters-title" className="text-[15px] font-semibold tracking-tight text-ink">
              Advanced filters
            </h2>
            <p className="mt-0.5 text-[11.5px] text-ink-3">Narrow every card on this page. The date range stays in the top bar.</p>
          </div>
          <IconButton icon="close" label="Close" size="sm" onClick={onClose} className="-mr-1" />
        </div>

        <form
          className="scroll-thin flex-1 overflow-y-auto"
          onSubmit={(e) => {
            e.preventDefault();
            apply();
          }}
        >
          {show.includes("platform") && (
            <Section title="Platform">
              <Segmented mode="radio" label="Platform" options={PLATFORMS} value={draft.platform} onChange={set("platform")} size="md" block />
            </Section>
          )}

          {show.includes("profile") && (
            <Section title="Audience" hint="The reader's profile, not the content's topic. Options are this tenant's active users in the selected dates.">
              {/* Country only for tenants with a global audience; India-only tenants have one country. */}
              {PROFILE.filter(([k]) => k !== "country" || hasRegion(filters.tenant, "gl")).map(([k, all]) => (
                <Field key={k} label={FILTER_LABELS[k]}>
                  <OptionSelect value={draft[k]} options={o(k)} all={all} onChange={set(k)} disabled={opts.isLoading} />
                </Field>
              ))}
            </Section>
          )}

          {show.includes("format") && (
            <Section title="Format" hint="One content format, on pages that compare formats.">
              <Field label={FILTER_LABELS.contentType}>
                <Select size="sm" value={draft.contentType} onChange={(e) => set("contentType")(e.target.value)} disabled={opts.isLoading}>
                  <option value="">All formats</option>
                  {o("contentType").map((x) => (
                    <option key={x.value} value={x.value}>
                      {x.label ?? x.value}
                    </option>
                  ))}
                </Select>
              </Field>
            </Section>
          )}

          {show.includes("content") && (
            <Section title="Content" hint="The item's own speciality tags, whoever reads it. One item can carry several tags.">
              <Field label={FILTER_LABELS.contentSpecialty}>
                <OptionSelect value={draft.contentSpecialty} options={o("contentSpecialty")} all="All content specialities" onChange={set("contentSpecialty")} disabled={opts.isLoading} />
              </Field>
            </Section>
          )}

          {show.includes("utm") && (
            <Section title="Campaign" hint="Exact values. Shortlinks carry the campaign in utm_source.">
              {["source", "medium", "campaign"].map((k) => (
                <Field key={k} label={FILTER_LABELS[k]}>
                  <Input size="sm" value={draft[k]} placeholder="Any" onChange={(e) => set(k)(e.target.value)} />
                </Field>
              ))}
            </Section>
          )}

          <Section title="Population">
            <Checkbox
              label="Include test users"
              description="Internal and demo accounts are excluded by default."
              checked={draft.includeTest}
              onChange={(e) => set("includeTest")(e.target.checked)}
            />
          </Section>

          {/* Enter in a text field applies. */}
          <button type="submit" hidden aria-hidden tabIndex={-1} />
        </form>

        <div className="flex items-center gap-2 border-t border-line bg-surface-2/50 px-5 py-3">
          <button type="button" onClick={reset} className="text-[12px] font-medium text-ink-3 underline-offset-2 hover:text-ink hover:underline">
            Clear all
          </button>
          <div className="ml-auto flex gap-2">
            <Button size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" onClick={apply} disabled={!changed}>
              Apply filters
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
